"use client";

import { useCallback, useMemo, useState } from "react";
import { Badge, Button, HStack, Text, VStack } from "@chakra-ui/react";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import type { MeiliOrigin } from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import {
  MEILI_SEARCH_CONFIG,
  type MeiliDocumentSearchHit,
  MemoizedSearchIndex,
  MyInfiniteHitsGeneric,
  SearchQueryConnector,
  type SearchHitRendererProps,
  usePreservedLoadedPageCount,
} from "@/features/search";
import {
  DEFAULT_HITS_PER_PAGE,
  getDefaultSearchSortValue,
} from "@/features/search/config/meiliSearchConfig";
import {
  SearchClearLanguageFiltersButton,
  SearchHitsPerPageSelector,
  SearchLanguageRefinements,
  SearchSemanticRatioSlider,
} from "@/features/search/components/MeiliSearchControls";
import {
  useBacklogSearchControls,
  useMeiliRefreshState,
} from "@/state/globalStore";
import { CONTENT_ITEM_HOVER_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import ContentItemCard from "@/shared/ui/display/ContentItemCard";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import appSettings from "@/shared/config/appSettings";
import { getHumanReadableTimestamp } from "@/shared/utils";
import {
  PROFILE_SHAPING_EMPTY_STATES,
  ProfileReviewSearchInput,
} from "@/features/profile-shaping";
import OriginItemPreview from "./OriginItemPreview";

type SourceMaterialPickerProps = {
  onSelect: (origin: MeiliOrigin) => void;
};

type SourceMaterialPickerHitProps = SearchHitRendererProps<MeiliOrigin>;

function SourceMaterialPickerHit({
  hit,
  config,
  onAnchorAction,
}: SourceMaterialPickerHitProps) {
  const origin = hit as MeiliOrigin;
  const wasProcessed = origin.processed_status === "PROCESSED";

  return (
    <ContentItemCard
      bg="moveon.surface.bg"
      borderWidth="1"
      borderColor="moveon.surface.border"
      _hover={CONTENT_ITEM_HOVER_STYLES}
    >
      <VStack align="stretch" gap="3">
        <HStack justify="space-between" align="start" gap="3">
          <HStack gap="2" align="center" flexWrap="wrap">
            <Badge
              variant="surface"
              colorPalette={wasProcessed ? "green" : "orange"}
            >
              {wasProcessed ? "Source shaped" : "Source material"}
            </Badge>
            <Badge variant="surface" colorPalette="gray">
              {origin.language}
            </Badge>
          </HStack>
          <Text fontSize="xs" color="moveon.text.muted" whiteSpace="nowrap">
            {getHumanReadableTimestamp(origin.latest_at)}
          </Text>
        </HStack>

        <OriginItemPreview
          config={config}
          hit={hit}
          isInSearchContext={true}
          origin={origin}
          variant="compact"
        />

        <HStack justify="flex-end">
          <Button
            type="button"
            size="sm"
            bg="moveon.primary"
            color="white"
            onClick={() => {
              onAnchorAction?.(hit);
            }}
            aria-label="Shape selected source material"
          >
            <GenericIcon icon="extract" />
            <Text as="span" display={{ base: "none", sm: "inline" }}>
              Shape selected source
            </Text>
          </Button>
        </HStack>
      </VStack>
    </ContentItemCard>
  );
}

export default function SourceMaterialPicker({
  onSelect,
}: SourceMaterialPickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const backlogControls = useBacklogSearchControls();
  const searchConfig = MEILI_SEARCH_CONFIG.origins;
  const { searchClient } = useMeiliRuntime({ surface: "backlog" });
  const languageFilterKey = backlogControls.filters.language.join(":");
  const defaultSearchSortBy = getDefaultSearchSortValue(searchConfig);
  const hasLanguageFilters = backlogControls.filters.language.length > 0;
  const hasActiveQuery = searchQuery.trim().length > 0;
  const hasNonDefaultSearchAdjustments =
    backlogControls.hitsPerPage !== appSettings.search.HITS_PER_PAGE ||
    backlogControls.sortBy !== defaultSearchSortBy ||
    backlogControls.semanticRatio !== 0.5 ||
    hasLanguageFilters;
  const searchKey = useMemo(
    () =>
      [
        meiliRefreshStamp.backlog_origins,
        backlogControls.hitsPerPage,
        backlogControls.sortBy,
        backlogControls.semanticRatio,
        languageFilterKey,
      ].join("|"),
    [
      backlogControls.hitsPerPage,
      backlogControls.semanticRatio,
      backlogControls.sortBy,
      languageFilterKey,
      meiliRefreshStamp.backlog_origins,
    ],
  );
  const preserveLoadedPagesKey = [
    "source-material-picker",
    searchQuery.trim(),
    backlogControls.hitsPerPage,
    backlogControls.sortBy,
    backlogControls.semanticRatio,
    languageFilterKey,
  ].join("|");
  const { loadedPageCount, loadMore } = usePreservedLoadedPageCount(
    preserveLoadedPagesKey,
  );
  const handleLanguageToggle = useCallback(
    (language: string) => {
      const currentLanguages = backlogControls.filters.language;
      const nextLanguages = currentLanguages.includes(language)
        ? currentLanguages.filter(
            (currentLanguage) => currentLanguage !== language,
          )
        : [...currentLanguages, language];

      backlogControls.setFilters({ language: nextLanguages });
    },
    [backlogControls],
  );
  const handleClearLanguageFilters = useCallback(() => {
    backlogControls.setFilters({ language: [] });
  }, [backlogControls]);

  if (!searchClient) {
    return null;
  }

  return (
    <VStack align="stretch" gap="3">
      <ProfileReviewSearchInput
        ariaLabel="Search source material"
        onOpenSearchSettings={() => {
          setIsSettingsOpen((current) => !current);
        }}
        onQueryChange={setSearchQuery}
        onResetQuery={() => {
          setSearchQuery("");
        }}
        placeholder="Search source material"
        query={searchQuery}
        searchSettingsHighlighted={
          isSettingsOpen || hasNonDefaultSearchAdjustments
        }
        searchSettingsLabel="Source search settings"
        voiceSearchSource="backlog"
      />

      {isSettingsOpen ? (
        <VStack
          align="stretch"
          gap="3"
          p="3"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          bg="moveon.surface.bg"
        >
          <HStack justify="space-between" gap="3">
            <Text fontSize="sm" fontWeight="semibold">
              Source search settings
            </Text>
            <Button
              size="sm"
              variant="ghost"
              disabled={!hasNonDefaultSearchAdjustments}
              onClick={backlogControls.resetSearchAdjustments}
            >
              <GenericIcon
                icon="reset"
                size="sm"
                highlighted={hasNonDefaultSearchAdjustments}
                humanReadable="Reset source search settings"
              />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Reset
              </Text>
            </Button>
          </HStack>

          <SearchSemanticRatioSlider
            stateObject={[backlogControls.semanticRatio]}
            stateObjectSetter={(values: number[]) => {
              if (values.length) {
                backlogControls.setSemanticRatio(values[0]);
              }
            }}
            title="Semantic-to-keyword search ratio"
            isDecimal
          />

          <SearchHitsPerPageSelector
            value={backlogControls.hitsPerPage}
            onChange={(value: number) => {
              backlogControls.setHitsPerPage(value);
            }}
            items={DEFAULT_HITS_PER_PAGE.map((item) => ({
              value: parseInt(item.value, 10),
              label: item.label,
            }))}
          />

          {!hasActiveQuery ? (
            <GenericSelector
              previewItems={searchConfig.sortBy}
              stateObject={[backlogControls.sortBy]}
              stateObjectSetter={(values: string[]) => {
                if (values.length) {
                  backlogControls.setSortBy(values[0]);
                }
              }}
              labelText="Sort by (when not in search)"
              mode="select"
              placeholder="Sort by..."
              disableClear
            />
          ) : null}

          <SearchLanguageRefinements
            selectedLanguages={backlogControls.filters.language}
            onLanguageToggle={handleLanguageToggle}
            title="Filter by language"
          />

          <SearchClearLanguageFiltersButton
            onClear={handleClearLanguageFilters}
            hasFilters={hasLanguageFilters}
          />
        </VStack>
      ) : null}

      <InstantSearchNext
        key={searchKey}
        indexName={searchConfig.indexName}
        searchClient={searchClient}
        routing={false}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <MemoizedSearchIndex
          indexName={searchConfig.indexName}
          indexId="source-material-picker"
          hitsPerPage={backlogControls.hitsPerPage * loadedPageCount}
          mainContentField={searchConfig.mainContentField}
          previewLength={appSettings.search.SEARCH_RESULT_PREVIEW_LENGTH}
          sortBy={backlogControls.sortBy}
          languageFilters={backlogControls.filters.language}
        >
          <SearchQueryConnector
            query={searchQuery.trim()}
            debounceMs={250}
            debugLabel="source-material-picker"
          />
          <MyInfiniteHitsGeneric
            config={searchConfig}
            renderHit={SourceMaterialPickerHit}
            indexId="source-material-picker"
            onAnchorAction={(hit: MeiliDocumentSearchHit) => {
              onSelect(hit as MeiliOrigin);
            }}
            onLoadMoreAction={loadMore}
            emptyState={{
              ...PROFILE_SHAPING_EMPTY_STATES.backlogSource,
              icon: "origin_source",
              variant: "inline",
            }}
          />
        </MemoizedSearchIndex>
      </InstantSearchNext>
    </VStack>
  );
}
